import React from "react";
import {Checkbox, Col, DatePicker, Divider, Form, Input, InputNumber, Modal, Row, Select} from "antd";
import {useTranslate} from "../../../../../../../../hooks/translate.hook";
import {AgreementData} from "../../common/model";
import dayjs from "dayjs";

interface AgreementEditModalProps {
    item: AgreementData | null,
    onClose: () => void,
    onComplete: (item: AgreementData) => void,
    warehouses: Record<string, string>,

}

export const AgreementEditModal: React.FC<AgreementEditModalProps> = ({item, onClose, onComplete, warehouses}) => {
    const t = useTranslate();

    const [form] = Form.useForm();

    let defaultValues: any = {
        maxAmount: 100,
        transactionType: "regular",
    }
    if (item) {
        defaultValues = {
            ...item,
            maxAmount: item.maxAmount ?? 100,
            transactionType: item.transactionType ?? "regular",
        }
        defaultValues.date = dayjs(item.date);
    }

    return (
        <Modal
            open
            title={item ? t("EDIT") + ' ' + (warehouses[item.warehouse] || 'warehouse') : t("ADD")}
            okText={t("SAVE")}
            cancelText={t("CANCEL")}
            onOk={() => form.submit()}
            centered
            onCancel={() => {
                onClose();
                form.resetFields();
            }}
        >
            <Form
                form={form}
                layout="vertical"
                name={"order_data"}
                initialValues={defaultValues}
                onFinish={() => form
                    .validateFields()
                    .then(values => {
                        const result = {...values}
                        if (item) {
                            result.warehouse = item.warehouse;
                            result.sort = item.sort;
                        } else {
                            result.sort = -1;
                        }
                        onComplete(result);
                        onClose();
                    })
                }
            >
                <Divider orientation="left" plain>
                    {t("CLIENT.WAREHOUSE.DETAILS", {defaultValue: "Warehouse details"})}
                </Divider>
                {item ? null : (
                    <Row gutter={10}>
                        <Col span={24}>
                            <Form.Item
                                name="warehouse"
                                label={t("WAREHOUSES.WAREHOUSE_TITLE")}
                                rules={[{required: true, message: t('FROM.ERROR.PLEASE_ENTER_VALUE')}]}
                            >
                                <Select>
                                    {Object.keys(warehouses).map((code) => (
                                        <Select.Option key={code}>{warehouses[code]}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                )}
                <Row gutter={10}>
                    <Col span={12}>
                        <Form.Item
                            name="maxAmount"
                            label={t("ACCOUNT.SETTINGS.WAREHOUSE.MAX_AMOUNT.TITLE")}
                            rules={[{required: true, message: t('FROM.ERROR.PLEASE_ENTER_VALUE')}]}
                        >
                            <InputNumber style={{width: "100%"}} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="transactionType"
                            label={t("ACCOUNT.SETTINGS.WAREHOUSE.TRANSACTION_TYPE.TITLE")}
                            rules={[{required: true, message: t('FROM.ERROR.PLEASE_ENTER_VALUE')}]}
                        >
                            <Select
                                placeholder={t("INVOICING.TRANSACTION_TYPE.TITLE")}
                            >
                                {["regular", "triangular"].map(item => (
                                    <Select.Option
                                        key={item}
                                        value={item}
                                    >
                                        {item}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={10}>
                    <Col span={24}>
                        <Form.Item
                            name="readonly"
                            label={t("ACCOUNT.SETTINGS.WAREHOUSE.READONLY.TITLE")}
                            valuePropName={"checked"}
                        >
                            <Checkbox />
                        </Form.Item>
                    </Col>
                </Row>
                <Divider orientation="left" plain>
                    {t("CLIENT.WAREHOUSE.AGREEMENT.TITLE", {defaultValue: "Warehouse agreement"})}
                </Divider>
                <Row gutter={10}>
                    <Col span={12}>
                        <Form.Item
                            name="number"
                            label={t("NUMBER")}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="date"
                            label={t("DATE")}
                        >
                            <DatePicker placeholder={t("DATE")} style={{width: "100%"}} />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
}
